import React, { useEffect, useRef, useState } from "react";
import {
  ArrowLeftOutlined,
  CloudUploadOutlined,
  DeleteOutlined,
  LoadingOutlined,
  PlusOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import { Link, useNavigate, useParams } from "react-router-dom";
import adminApi from "../api/adminApi";
import ConfirmDialog from "./ConfirmDialog";

const emptyForm = { projectId: "", type: "", location: "", owner: "", capacity: "", inverter: "", panel: "", images: [] };

export default function ProjectForm() {
  const { id } = useParams();
  const editing = Boolean(id);
  const navigate = useNavigate();
  const fileInput = useRef(null);
  const [form, setForm] = useState(emptyForm);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [removeTarget, setRemoveTarget] = useState(null);

  useEffect(() => {
    let active = true;
    Promise.all([
      adminApi.getProjectCategories(),
      editing ? adminApi.getProject(id) : Promise.resolve(null),
    ]).then(([categoryData, project]) => {
      if (!active) return;
      setCategories(categoryData || []);
      if (project) setForm({ ...emptyForm, ...project, images: project.images || [] });
    }).catch((err) => setError(adminApi.getErrorMessage(err, "Could not load project form")))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [editing, id]);

  function setField(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  async function uploadFiles(event) {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;
    const invalid = files.find((file) => !file.type.startsWith("image/"));
    if (invalid) return setError(`${invalid.name} is not an image file.`);
    setError("");
    setUploading(true);
    setUploadProgress(0);
    const uploaded = [];
    try {
      for (let index = 0; index < files.length; index += 1) {
        const result = await adminApi.uploadImage(files[index], (progress) => {
          const portion = progress.total ? progress.loaded / progress.total : 0;
          setUploadProgress(Math.round(((index + portion) / files.length) * 100));
        });
        if (!result?.url) throw new Error("The upload completed without an image URL");
        uploaded.push(result.url);
      }
      setForm((current) => ({ ...current, images: [...current.images, ...uploaded] }));
      setUploadProgress(100);
    } catch (err) {
      setError(adminApi.getErrorMessage(err, `Upload failed after ${uploaded.length} of ${files.length} images`));
    } finally {
      setUploading(false);
    }
  }

  async function removeImage() {
    const { url, index } = removeTarget;
    setRemoveTarget(null);
    setError("");
    try {
      if (editing) await adminApi.deleteProjectImage(id, url);
      setForm((current) => ({ ...current, images: current.images.filter((_, itemIndex) => itemIndex !== index) }));
    } catch (err) {
      setError(adminApi.getErrorMessage(err, "The image could not be removed"));
    }
  }

  async function submit(event) {
    event.preventDefault();
    if (!form.type.trim()) {
      setError("Project category / type is required.");
      return;
    }
    setSaving(true);
    setError("");
    setSuccess("");
    const payload = ["projectId", "type", "location", "owner", "capacity", "inverter", "panel", "images"]
      .reduce((data, key) => ({ ...data, [key]: typeof form[key] === "string" ? form[key].trim() : form[key] }), {});
    try {
      if (editing) {
        const updated = await adminApi.updateProject(id, payload);
        setForm({ ...emptyForm, ...updated, images: updated.images || [] });
        setSuccess("Project changes saved.");
      } else {
        await adminApi.createProject(payload);
        navigate("/admin/projects", { replace: true });
      }
    } catch (err) {
      setError(adminApi.getErrorMessage(err, "Project could not be saved"));
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="admin-skeleton form" />;

  return (
    <div className="admin-page-stack">
      <Link to="/admin/projects" className="admin-back-link"><ArrowLeftOutlined /> Back to projects</Link>
      {error && <div className="admin-alert error">{error}</div>}
      {success && <div className="admin-alert success">{success}</div>}
      <form onSubmit={submit} className="admin-form-layout">
        <div className="admin-form-main">
          <section className="admin-panel admin-form-section">
            <div className="admin-section-heading"><span>01</span><div><h3>Project information</h3><p>Core details shown across the project portfolio.</p></div></div>
            <div className="admin-form-grid">
              <Field label="Project ID" hint="A unique business reference, e.g. SP-001"><input value={form.projectId} onChange={(e) => setField("projectId", e.target.value)} placeholder="SP-001" /></Field>
              <Field label="Category / type" required>
                <select value={form.type} onChange={(e) => setField("type", e.target.value)} required>
                  <option value="">Select a category</option>
                  {categories.map((category) => <option value={category.name} key={category._id}>{category.name}</option>)}
                  {form.type && !categories.some((category) => category.name === form.type) && <option value={form.type}>{form.type} (existing)</option>}
                </select>
              </Field>
              <Field label="Client / owner"><input value={form.owner} onChange={(e) => setField("owner", e.target.value)} placeholder="Customer or organization" /></Field>
              <Field label="Location"><input value={form.location} onChange={(e) => setField("location", e.target.value)} placeholder="City or district" /></Field>
            </div>
          </section>
          <section className="admin-panel admin-form-section">
            <div className="admin-section-heading"><span>02</span><div><h3>System specification</h3><p>Record the installed system and primary components.</p></div></div>
            <div className="admin-form-grid">
              <Field label="Capacity"><input value={form.capacity} onChange={(e) => setField("capacity", e.target.value)} placeholder="e.g. 10 kW" /></Field>
              <Field label="Inverter"><input value={form.inverter} onChange={(e) => setField("inverter", e.target.value)} placeholder="Brand and model" /></Field>
              <Field label="Solar panels" wide><input value={form.panel} onChange={(e) => setField("panel", e.target.value)} placeholder="Brand, wattage, and quantity" /></Field>
            </div>
          </section>
          <section className="admin-panel admin-form-section">
            <div className="admin-section-heading"><span>03</span><div><h3>Project gallery</h3><p>Upload clear installation photos. Each file is uploaded before the project is saved.</p></div></div>
            <button type="button" className={`admin-dropzone ${uploading ? "busy" : ""}`} onClick={() => !uploading && fileInput.current?.click()}>
              {uploading ? <LoadingOutlined spin /> : <CloudUploadOutlined />}
              <strong>{uploading ? `Uploading images… ${uploadProgress}%` : "Choose project images"}</strong>
              <span>PNG, JPG or WEBP · multiple images supported</span>
              {uploading && <i style={{ width: `${uploadProgress}%` }} />}
            </button>
            <input ref={fileInput} type="file" accept="image/*" multiple hidden onChange={uploadFiles} />
            {form.images.length > 0 && <div className="admin-image-grid">
              {form.images.map((url, index) => <div className="admin-image-tile" key={`${url}-${index}`}><img src={url} alt={`Project ${index + 1}`} /><span>{index + 1}</span><button type="button" onClick={() => setRemoveTarget({ url, index })} aria-label="Remove image"><DeleteOutlined /></button></div>)}
              <button type="button" className="admin-add-image" onClick={() => fileInput.current?.click()} disabled={uploading}><PlusOutlined /><span>Add more</span></button>
            </div>}
          </section>
        </div>
        <aside className="admin-form-aside">
          <div className="admin-panel admin-save-card"><h3>{editing ? "Save changes" : "Ready to publish?"}</h3><p>{editing ? "Updates will appear in the project portfolio immediately." : "Review the details before adding this project."}</p><button className="admin-primary-button" disabled={saving || uploading}>{saving ? <><LoadingOutlined spin /> Saving…</> : <><SaveOutlined /> {editing ? "Save changes" : "Create project"}</>}</button><Link to="/admin/projects" className="admin-secondary-button">Cancel</Link></div>
          <div className="admin-form-tip"><strong>Helpful tip</strong><p>Create and manage selectable project types from the Categories page.</p><Link to="/admin/categories">Manage categories →</Link></div>
        </aside>
      </form>
      <ConfirmDialog open={Boolean(removeTarget)} title="Remove this image?" description={editing ? "The image URL will be removed from the project and Cloudinary deletion will be attempted." : "The image will be removed from this new project."} confirmLabel="Remove image" onCancel={() => setRemoveTarget(null)} onConfirm={removeImage} />
    </div>
  );
}

function Field({ label, hint, required, wide, children }) {
  return <label className={`admin-field ${wide ? "wide" : ""}`}><span>{label}{required && <b>*</b>}</span>{children}{hint && <small>{hint}</small>}</label>;
}
